import { JSX } from 'react';
import { ContentContainer, Spinner, Text } from '@ahc-digital/component-library';
import { Text as JSSText } from '@sitecore-jss/sitecore-jss-nextjs';
import Script from 'next/script';
import { useState } from 'react';
import { PromptForErrorMessage } from 'src/shared/PromptForErrorMessage';
import { SwitchToPreviewMessage } from 'src/shared/SwitchToPreviewMessage';
import { useCustomSitecoreContext } from 'src/shared/useCustomSitecoreContext';
import { I01SparkleProviderOrLocationSearchProps } from './I01SparkleProviderOrLocationSearch.types';
import { translationClasses } from 'src/shared/TranslationClasses';
import { useCreateMarketingRenderingHame } from 'src/shared/useCreateMarketingRenderingName';
import { checkDatasourceForRendering } from '@/shared/checkDatasourceForRendering';

const Default = (props: I01SparkleProviderOrLocationSearchProps): JSX.Element => {
  const [isError, setIsError] = useState(false);
  const context = useCustomSitecoreContext();
  const isEditing = context?.pageEditing ?? false;
  const [isJQueryLoading, setIsJQueryLoading] = useState(true);
  const [isSparkleLoading, setIsSparkleLoading] = useState(true);
  const sparkleApiBaseUrl = context?.sparkleApiBaseUrl?.value;
  const marketingRenderingName = useCreateMarketingRenderingHame(
    props?.rendering?.componentName as string
  );

  if (!sparkleApiBaseUrl) {
    return (
      <PromptForErrorMessage
        componentName="I01 - Sparkle Provider or Location Search"
        message="Sparkle API base URL not found."
      />
    );
  }

  if (isEditing) {
    return (
      <SwitchToPreviewMessage componentName={props?.rendering?.componentName}>
        <ContentContainer
          spacing="40-24"
          supportCopyAlignment="left"
          supportCopy={
            <>
              <h4>Configuration Details:</h4>
              <p>
                <b>Sparkle API base URL:</b> {sparkleApiBaseUrl} <br />
              </p>
            </>
          }
        >
          <Text tag="p" fontStyle="smParagraphBold">
            UUID:
          </Text>
          <br />
          <JSSText field={props?.fields?.UUID} />
          <Text tag="p" fontStyle="smParagraphBold">
            Container ID:
          </Text>
          <br />
          <JSSText field={props?.fields?.ContainerID} />
        </ContentContainer>
      </SwitchToPreviewMessage>
    );
  }

  const uuid = props?.fields?.UUID?.value;
  const containerid = props?.params?.AnchorLinkTargetName
    ? props?.params?.AnchorLinkTargetName
    : props?.fields?.ContainerID?.value || 'sparkle-search-container';

  if (!uuid) {
    return <></>;
  }

  return (
    <>
      <Script
        src="https://code.jquery.com/jquery-3.7.1.min.js"
        integrity="sha256-/JqT3SQfawRcv/BIHPThkBvs0OEvtFFmqPF/lYI/Cxo="
        crossOrigin="anonymous"
        onError={() => {
          setIsError(true);
        }}
        onReady={() => {
          setIsJQueryLoading(false);
        }}
      />
      <div
        className={translationClasses(props?.params)}
        id={containerid}
        data-component={marketingRenderingName}
      >
        {(isSparkleLoading || isJQueryLoading) && !isError && (
          <ContentContainer spacing="40-24">
            <Spinner />
          </ContentContainer>
        )}

        {isError && <></>}
      </div>
      {!isJQueryLoading && !isError && (
        <Script
          src={`${sparkleApiBaseUrl}/assets/embed/doctor_finder.js`}
          onError={() => {
            setIsError(true);
          }}
          onReady={() => {
            if (isError) return;
            new Sparkle.Embed.DoctorFinder(containerid, {
              uuid,
              search: {
                specialty_scope:
                  (document.getElementById('hdnSpecialtyNames') as HTMLInputElement)?.value || '',
                location_scope:
                  (document.getElementById('hdnProviderLocations') as HTMLInputElement)?.value ||
                  '',
                clinical_interest_scope:
                  (document.getElementById('hdnClinicalInterests') as HTMLInputElement)?.value ||
                  '',
                provider_type_scope:
                  (document.getElementById('hdnProviderCareTypeNames') as HTMLInputElement)
                    ?.value || '',
                employed: (document.getElementById('hdnOwned') as HTMLInputElement)?.value || '',
                age_group:
                  (document.getElementById('hdnPatientAgeGroup') as HTMLInputElement)?.value || '',
                skill: (document.getElementById('hdnSkill') as HTMLInputElement)?.value || '',
                provider_network_scope:
                  (document.getElementById('hdnProviderNetwork') as HTMLInputElement)?.value || '',
              },
            })
              .then(() => {
                setIsSparkleLoading(false);
              })
              .catch(() => {
                setIsSparkleLoading(false);
                setIsError(true);
              });
          }}
        />
      )}
    </>
  );
};

export default checkDatasourceForRendering<I01SparkleProviderOrLocationSearchProps>(Default);
